import { Check, ChevronDown } from 'lucide-react';
import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

export function Field({
  label,
  htmlFor,
  hint,
  error,
  optional,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="dui-field">
      <div className="dui-field__label-row">
        <label htmlFor={htmlFor}>{label}</label>
        {optional ? <span>Optional</span> : null}
      </div>
      {children}
      {error ? (
        <p className="dui-field__message dui-field__message--error" id={`${htmlFor}-error`}>
          {error}
        </p>
      ) : hint ? (
        <p className="dui-field__message" id={`${htmlFor}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...props }, ref) {
    return (
      <input ref={ref} className={['dui-input', className].filter(Boolean).join(' ')} {...props} />
    );
  },
);

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function TextArea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={['dui-input dui-textarea', className].filter(Boolean).join(' ')}
      {...props}
    />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { wrapperClassName?: string }
>(function Select({ className, wrapperClassName, children, ...props }, ref) {
  return (
    <span className={['dui-select-wrap', wrapperClassName].filter(Boolean).join(' ')}>
      <select
        ref={ref}
        className={['dui-input dui-select', className].filter(Boolean).join(' ')}
        {...props}
      >
        {children}
      </select>
      <ChevronDown size={15} aria-hidden="true" />
    </span>
  );
});

export function Checkbox({
  label,
  description,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; description?: string }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <label className="dui-check-control" htmlFor={id}>
      <input {...props} id={id} type="checkbox" />
      <span className="dui-check-control__box" aria-hidden="true">
        <Check size={13} />
      </span>
      <span>
        <strong>{label}</strong>
        {description ? <small>{description}</small> : null}
      </span>
    </label>
  );
}

export function Radio({
  label,
  description,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; description?: string }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <label className="dui-radio-control" htmlFor={id}>
      <input {...props} id={id} type="radio" />
      <span className="dui-radio-control__circle" aria-hidden="true" />
      <span>
        <strong>{label}</strong>
        {description ? <small>{description}</small> : null}
      </span>
    </label>
  );
}

export function Toggle({
  label,
  description,
  checked,
  defaultChecked,
  disabled,
  hideLabel = false,
  tone = 'action',
  onCheckedChange,
}: {
  label: string;
  description?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  tone?: 'action' | 'visible';
  onCheckedChange?(checked: boolean): void;
}) {
  const [internal, setInternal] = useState(defaultChecked ?? false);
  const current = checked ?? internal;
  return (
    <button
      className="dui-toggle-row"
      data-tone={tone}
      type="button"
      role="switch"
      aria-label={hideLabel ? label : undefined}
      aria-checked={current}
      disabled={disabled}
      onClick={() => {
        const next = !current;
        if (checked === undefined) setInternal(next);
        onCheckedChange?.(next);
      }}
    >
      {hideLabel ? null : (
        <span>
          <strong>{label}</strong>
          {description ? <small>{description}</small> : null}
        </span>
      )}
      <span className="dui-toggle" aria-hidden="true">
        <span />
      </span>
    </button>
  );
}

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { value: T; label: string; icon?: ReactNode }[];
  value: T;
  onChange(value: T): void;
}) {
  return (
    <div className="dui-segmented" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}
